import React, { useState } from "react";

import "./index.less";
import styles from "./index.module.less";

import { Input, message } from "antd";
import { AxiosResponse } from "axios";

import {
	ContentProps,
	ErrorType,
	FreshDeskTicketDescription,
	ISuccessButtonProps,
	JointContent,
	messageType,
	ServerErrorProps,
	ServerValidationErrorResponse
} from "./interface";
import getTechnographics from "./utils";
import { ArgsProps, ConfigOnClose, MessageType } from "antd/lib/message";
import uniqueId from "lodash/uniqueId";

import r2curl from "r2curl";
import ZButton from "@atoms/ZButton";
import ZModal from "@atoms/ZModal";
import {
	DefaultErrorMessage,
	ZomentumWindow,
	CreateFreshDeskTicketRequest
} from "@/Contracts";
import { createFreshDeskTicket } from "./service";
import ZTypography from "@atoms/ZTypography";
import ZIcon from "@atoms/ZIcon";
import { EIcons } from "@atoms/ZIcon/interface";
import { EColors } from "@atoms/ZColors/interface";
import { isVendorApp } from "@/Utils";

declare const window: ZomentumWindow;

const { ZText } = ZTypography;

/*
	filterPaths are those routes where we don't want to show Need Help Button
	E.g.,: public routes like document view page for client
*/
const filterPaths = [
	"/settings/document-approval",
	"/settings/document-approval/:id",
	"/settings/document-approval/create",
	"/success/signature",
	"/documents/:id",
	"/authorize"
].map(route =>
	route.slice(
		0,
		route.indexOf(":") !== -1 ? route.indexOf(":") : route.length
	)
);

const Content = ({
	className,
	uniqueKey,
	type,
	content,
	onClose
}: ContentProps) => {
	return (
		<>
			<div className={`${className} ${styles.zMessageWrap} `}>
				<div
					key={`ZMessage-${uniqueKey}`}
					className={`${styles.container} ${styles.messageWrap} ${
						styles["errorType_" + type]
					} py-2 px-4 flex justify-between items-center rounded-md`}
				>
					<div className="w-full">
						<div className={`flex justify-between items-center`}>
							{typeof content === "string" ? (
								<ZText
									color={EColors.GRAY}
									colorVariant={1}
									className={`${styles.zText} mr-8 text-2 whitespace-nowrap`}
								>
									{content}
								</ZText>
							) : (
								content
							)}
						</div>
					</div>
					<div
						className={`flex justify-center items-center ml-2`}
						onClick={() => {
							message.destroy(uniqueKey);
							if (onClose) onClose();
						}}
					>
						<ZIcon
							name={EIcons.CLOSE}
							className={`cursor-pointer`}
							color={EColors.GRAY}
							colorVariant={1}
							fill
						/>
					</div>
				</div>
			</div>
		</>
	);
};

const NeedHelpButton = ({
	errorDetails
}: { errorDetails: string }): JSX.Element => {
	const handleNeedHelp = () => {
		if (isVendorApp()) {
			// shows FreshworksWidget
			window.FreshworksWidget("open");
		} else {
			document.dispatchEvent(new CustomEvent("openHubSpotConversation"));
			// Shows freshchat pop-up
			window?.fcWidget?.open({
				name: "Support",
				replyText: `Something went wrong, please help me. Here are the details: ${errorDetails}`
			});
		}
	};

	return (
		<ZButton
			className={`${styles.dangerButton} ml-2 text-1 px-3 h-8 font-bold`}
			onClick={handleNeedHelp}
		>
			Need Help
		</ZButton>
	);
};

const error = (
	content: JointContent,
	needHelpButtonVisible = true,
	duration?: number | null,
	onClose?: ConfigOnClose,
	calledFromServerError?: boolean
): MessageType => {
	const args = content as ArgsProps;

	const {
		className,
		key,
		content: contentArgs,
		duration: durationArgs,
		onClose: onCloseArgs
	} = args;

	const uniqueKey = uniqueId("error-message");

	const currentLocationPath = window.location.href;

	const showNeedHelpButton =
		!filterPaths.some(
			subPath =>
				currentLocationPath.includes(subPath) &&
				!currentLocationPath.includes("client_user_id")
		) && needHelpButtonVisible;

	return message.open({
		...args,
		type: "error",
		onClose: onCloseArgs ?? onClose,
		key: key ?? uniqueKey,
		duration: durationArgs ?? duration ?? 5,
		content: (
			<>
				<Content
					content={
						calledFromServerError ? (
							contentArgs ?? content
						) : (
							<>
								<div
									className={`flex justify-between items-center w-full`}
								>
									<ZText
										color={EColors.GRAY}
										colorVariant={1}
										className={`mr-4 text-2`}
									>
										{contentArgs ?? content}
									</ZText>
									{showNeedHelpButton && (
										<NeedHelpButton
											errorDetails={
												!!content &&
												typeof content === "string"
													? content
													: `Developer hasn't provided any error details`
											}
										/>
									)}
								</div>
							</>
						)
					}
					type={messageType.ERROR}
					uniqueKey={key?.toString() ?? uniqueKey}
				/>
			</>
		),
		className: `zMessage ${className}`
	});
};

const success = (
	content: JointContent,
	duration?: number | null,
	onClose?: ConfigOnClose,
	customCTA?: ISuccessButtonProps
): MessageType => {
	const args = content as ArgsProps;

	const {
		className,
		key,
		content: contentArgs,
		duration: durationArgs,
		onClose: onCloseArgs
	} = args;

	const uniqueKey = uniqueId("success-message");

	const contentToRender = customCTA ? (
		<div className={`flex justify-between items-center w-full`}>
			<ZText
				color={EColors.GRAY}
				colorVariant={1}
				className={`mr-8 text-1`}
			>
				{contentArgs ?? content}
			</ZText>
			<ZButton
				{...customCTA.buttonProps}
				className={`${styles.successButton} ${customCTA.buttonProps?.className} ml-2 text-1 px-3 h-7 font-medium`}
			>
				{customCTA.buttonText}
			</ZButton>
		</div>
	) : (
		contentArgs ?? content
	);

	return message.open({
		...args,
		type: "success",
		onClose: onCloseArgs ?? onClose,
		key: key ?? uniqueKey,
		duration: durationArgs ?? duration ?? 3,
		content: (
			<Content
				content={contentToRender}
				type={messageType.SUCCESS}
				uniqueKey={key?.toString() ?? uniqueKey}
			/>
		),
		className: `zMessage ${className}`
	});
};

const warning = (
	content: JointContent,
	duration?: number | null,
	onClose?: ConfigOnClose
): MessageType => {
	const args = content as ArgsProps;

	const {
		className,
		key,
		duration: durationArgs,
		onClose: onCloseArgs
	} = args;

	const uniqueKey = uniqueId("warning-message");

	return message.open({
		...args,
		type: "warning",
		onClose: onCloseArgs ?? onClose,
		key: key ?? uniqueKey,
		duration: durationArgs ?? duration ?? 3,
		content,
		className: `${className}`
	});
};

const serverError = ({
	errorData,
	reportData,
	currentLocationPath
}: ServerErrorProps): MessageType => {
	const code = errorData?.response?.status;

	const errorDetails =
		errorData.response?.data.message ||
		errorData.message ||
		DefaultErrorMessage;

	const codeType: ErrorType = code
		? Math.floor(code / 100) * 100
		: ErrorType.ERROR;
	// const codeType = 500;

	const {
		userData,
		currentActiveOpportunity,
		currentActiveReport,
		currentActivePipeline,
		currentActiveClient,
		userCompany
	} = reportData;

	const uniqueKey = uniqueId("server-error-message");

	const ServerContent = () => {
		const [modalVisible, setModalVisible] = useState(false);
		const [loading, setLoading] = useState(false);
		const [ticketText, setTicketText] = useState<string>("Not Provided");

		const handleOk = async (
			e: React.MouseEvent<HTMLElement, MouseEvent>
		) => {
			e.preventDefault();
			setLoading(true);

			const curl = r2curl(errorData.config as any);

			const ticketDescription: FreshDeskTicketDescription = {
				error_details: errorData.response?.data,
				curl,
				stacktrace: errorData.stack,
				context: {
					userId: userData.id,
					companyId: userData.company_id,
					opportunity: currentActiveOpportunity,
					report: currentActiveReport,
					pipeline: currentActivePipeline,
					client: currentActiveClient,

					userDescription: ticketText
				},
				technographics: getTechnographics()
			};

			try {
				const ticketRequest: CreateFreshDeskTicketRequest = {
					subject: (
						errorData?.response as AxiosResponse<ServerValidationErrorResponse>
					).data.message,
					description: JSON.stringify(ticketDescription, null, 2)
				};
				await createFreshDeskTicket(ticketRequest);

				message.destroy(uniqueKey);
				success("Ticket submitted, we'll be in touch!");

				setLoading(false);
				setModalVisible(false);
			} catch (e) {
				error(
					`Couldn't report because you might be logged out. Please click need help.`,
					undefined,
					userCompany,
					userData
				);
				console.error(e);
			}
		};

		const handleReportThis = () => {
			setModalVisible(true);
		};

		const handleCancel = () => {
			setModalVisible(false);
		};

		const showNeedHelpButton = !filterPaths.some(
			subPath =>
				currentLocationPath.includes(subPath) &&
				!currentLocationPath.includes("client_user_id")
		);

		return (
			<>
				<div className={`flex justify-between items-center w-full`}>
					<ZText
						color={EColors.GRAY}
						colorVariant={1}
						className={`mr-4 text-1`}
					>
						{errorDetails}
					</ZText>

					{codeType === ErrorType.ERROR_HELP &&
						showNeedHelpButton && (
							<NeedHelpButton errorDetails={errorDetails} />
						)}
					{codeType === ErrorType.ERROR_REPORT && (
						<ZButton
							className={`${styles.dangerButton} ml-2 text-1 px-3 h-8 font-bold`}
							onClick={handleReportThis}
						>
							Report this
						</ZButton>
					)}
				</div>

				<ZModal
					data-testid={`error-message-server-error-modal`}
					visible={modalVisible}
					title="Something went wrong, please submit a ticket to help us fix it."
					onOk={e => handleOk(e)}
					size="md"
					onCancel={handleCancel}
					footer={[
						<ZButton key="back" onClick={handleCancel}>
							Cancel
						</ZButton>,
						<ZButton
							key="submit"
							type="primary"
							loading={loading}
							onClick={handleOk}
						>
							Send
						</ZButton>
					]}
				>
					<p>Tell us what happened (Optional)</p>
					<Input.TextArea
						onChange={e => setTicketText(e.target.value)}
						rows={4}
					/>
				</ZModal>
			</>
		);
	};

	return error(
		{
			key: uniqueKey,
			content: <ServerContent />,
			className: `zMessage`
		},
		true,
		5,
		undefined,
		true
	);
};

export default { error, success, serverError, warning };
